import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useSearchParams, Link } from "react-router-dom";
import { Filter, X, SlidersHorizontal, Loader2, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import FilterPanel from "@/components/FilterPanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { filterProducts, facetsFrom, getCategory, getBrand } from "@/data/catalog";
import { fetchProducts } from "@/lib/api";
import { searchProducts } from "@/lib/search";
import SearchBar from "@/components/SearchBar";
import { SITE } from "@/data/site";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "name-asc", label: "Model / Name: A to Z" },
];

export default function Shop({ clearanceOnly = false }) {
  const { slug } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isCategoryRoute = location.pathname.startsWith("/category/");
  const isBrandRoute = location.pathname.startsWith("/brand/");

  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedRefrigerants, setSelectedRefrigerants] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("featured");
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchProducts(clearanceOnly ? { clearance: true } : undefined)
      .then((data) => setAllProducts(data))
      .finally(() => setLoading(false));
  }, [clearanceOnly]);

  useEffect(() => {
    setSelectedCategories(isCategoryRoute && slug ? [slug] : []);
    setSelectedBrands(isBrandRoute && slug ? [slug] : []);
    setSelectedTypes([]);
    setSelectedRefrigerants([]);
    setSearch(searchParams.get("q") || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, location.pathname]);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q !== null) setSearch(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const facets = useMemo(() => facetsFrom(allProducts), [allProducts]);
  const categoryCounts = useMemo(() => {
    const m = {};
    allProducts.forEach((p) => (p.categories || [p.categorySlug]).forEach((c) => (m[c] = (m[c] || 0) + 1)));
    return m;
  }, [allProducts]);

  const products = useMemo(
    () => {
      const base = search.trim() ? searchProducts(allProducts, search) : allProducts;
      const effectiveSort = search.trim() && sort === "featured" ? "relevance" : sort;
      return filterProducts(base, { categories: selectedCategories, brands: selectedBrands, types: selectedTypes, refrigerants: selectedRefrigerants, sort: effectiveSort });
    },
    [allProducts, selectedCategories, selectedBrands, selectedTypes, selectedRefrigerants, search, sort]
  );

  const toggle = (setter) => (val) => setter((prev) => (prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]));
  const toggleCategory = toggle(setSelectedCategories);
  const toggleBrand = toggle(setSelectedBrands);
  const toggleType = toggle(setSelectedTypes);
  const toggleRefrigerant = toggle(setSelectedRefrigerants);
  const clearSearch = () => {
    setSearch("");
    if (searchParams.get("q") !== null) setSearchParams({});
  };
  const clearAll = () => {
    setSelectedCategories([]);
    setSelectedBrands([]);
    setSelectedTypes([]);
    setSelectedRefrigerants([]);
    clearSearch();
    setSort("featured");
  };

  const heading = clearanceOnly
    ? "Clearance"
    : isCategoryRoute && getCategory(slug)
    ? getCategory(slug).name
    : isBrandRoute && getBrand(slug)
    ? `${getBrand(slug).name} Equipment`
    : search.trim()
    ? `Results for “${search.trim()}”`
    : "All Equipment";

  const subheading = clearanceOnly
    ? "Discounted HVAC units as they become available."
    : isCategoryRoute && getCategory(slug)
    ? getCategory(slug).blurb
    : isBrandRoute && getBrand(slug)
    ? getBrand(slug).tagline
    : search.trim()
    ? "Matching products by brand, category, name and model number."
    : "Browse every HVAC system in our catalog by category, brand, type and refrigerant.";

  const activeCount = selectedCategories.length + selectedBrands.length + selectedTypes.length + selectedRefrigerants.length;
  const hasActiveFilters = activeCount > 0 || Boolean(search.trim());

  const panel = (
    <FilterPanel
      selectedCategories={selectedCategories}
      selectedBrands={selectedBrands}
      selectedTypes={selectedTypes}
      selectedRefrigerants={selectedRefrigerants}
      facets={facets}
      categoryCounts={categoryCounts}
      onToggleCategory={toggleCategory}
      onToggleBrand={toggleBrand}
      onToggleType={toggleType}
      onToggleRefrigerant={toggleRefrigerant}
      onClear={clearAll}
    />
  );

  return (
    <div className="bg-slate-50">
      <Seo title={`${heading} | CA HVAC Direct`} description={`Shop ${heading.toLowerCase()} from CA HVAC Direct. Filter HVAC equipment by category, brand, product type and refrigerant.`} />

      {/* Department header band */}
      <div className="border-b border-slate-200 bg-[#0B1528]">
        <span className="thermo-bar block h-1 w-full" />
        <div className="mx-auto max-w-[1600px] px-4 py-8 sm:px-6 lg:px-8">
          <nav className="font-mono text-[11px] text-slate-400" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-sky-300">Home</Link>
            <span className="px-1.5">/</span>
            <Link to="/shop" className="hover:text-sky-300">Catalog</Link>
            {heading !== "All Equipment" && <><span className="px-1.5">/</span><span className="text-slate-300">{heading}</span></>}
          </nav>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">{heading}</h1>
              <p className="mt-1 max-w-2xl text-sm text-slate-400">{subheading}</p>
            </div>
            <p className="font-mono text-sm text-sky-300" data-testid="results-count">{products.length} {products.length === 1 ? "unit" : "units"}</p>
          </div>
          {!clearanceOnly && <div className="mt-5 max-w-2xl"><SearchBar initial={searchParams.get("q") || ""} size="sm" testId="shop-search" /></div>}
        </div>
      </div>

      <div className="mx-auto max-w-[1600px] px-4 py-8 sm:px-6 lg:px-8">
        {/* Mobile controls */}
        <div className="mb-6 flex items-center gap-3 lg:hidden">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="rounded-sm border-slate-300" data-testid="mobile-filter-button">
                <Filter className="h-4 w-4" /> Filters
                {activeCount > 0 && <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-sky-700 px-1 text-[11px] text-white">{activeCount}</span>}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[88vw] max-w-sm overflow-y-auto bg-white p-0">
              <SheetHeader className="border-b border-slate-200 p-5 text-left"><SheetTitle>Filters</SheetTitle></SheetHeader>
              <div className="p-5">{panel}</div>
            </SheetContent>
          </Sheet>
          <div className="ml-auto">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[175px] rounded-sm border-slate-300 bg-white" data-testid="sort-select-mobile"><SlidersHorizontal className="h-4 w-4 text-slate-400" /><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-[18rem_1fr]">
          <aside className="hidden lg:block"><div className="sticky top-28">{panel}</div></aside>

          <div>
            <div className="mb-5 hidden items-center justify-between gap-4 lg:flex">
              <div className="flex flex-wrap items-center gap-2">
                {hasActiveFilters ? (
                  <>
                    {selectedCategories.map((c) => <Badge key={c} variant="secondary" className="cursor-pointer rounded-sm" onClick={() => toggleCategory(c)} data-testid={`chip-category-${c}`}>{getCategory(c)?.name} <X className="ml-1 h-3 w-3" /></Badge>)}
                    {selectedBrands.map((b) => <Badge key={b} variant="secondary" className="cursor-pointer rounded-sm" onClick={() => toggleBrand(b)} data-testid={`chip-brand-${b}`}>{getBrand(b)?.name} <X className="ml-1 h-3 w-3" /></Badge>)}
                    {selectedTypes.map((t) => <Badge key={t} variant="secondary" className="cursor-pointer rounded-sm" onClick={() => toggleType(t)}>{t} <X className="ml-1 h-3 w-3" /></Badge>)}
                    {selectedRefrigerants.map((r) => <Badge key={r} variant="secondary" className="cursor-pointer rounded-sm" onClick={() => toggleRefrigerant(r)}>{r} <X className="ml-1 h-3 w-3" /></Badge>)}
                    {search.trim() && <Badge variant="secondary" className="cursor-pointer rounded-sm" onClick={clearSearch} data-testid="chip-search">Search: “{search}” <X className="ml-1 h-3 w-3" /></Badge>}
                    <button onClick={clearAll} className="ml-1 font-mono text-xs font-semibold text-sky-700 hover:underline" data-testid="clear-all-chips">Clear all</button>
                  </>
                ) : (
                  <span className="font-mono text-xs uppercase tracking-wide text-slate-500">Showing {clearanceOnly ? "clearance" : "all"} equipment</span>
                )}
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[200px] rounded-sm border-slate-300 bg-white" data-testid="sort-select"><SlidersHorizontal className="h-4 w-4 text-slate-400" /><SelectValue /></SelectTrigger>
                <SelectContent>{SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : products.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="empty-state">
                <p className="font-heading text-lg font-bold text-slate-900" data-testid="empty-state-title">{search.trim() ? `No products found for “${search.trim()}”` : hasActiveFilters ? "No equipment matches your filters" : "Equipment for this department is coming soon"}</p>
                <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{search.trim() ? "Check the spelling, try a shorter term, or search by brand, category or model number." : hasActiveFilters ? "Try adjusting or clearing your filters." : "Tell us what you're looking for and we'll source it for you."}</p>
                {search.trim() && (
                  <div className="mt-5 flex flex-wrap justify-center gap-2" data-testid="empty-suggestions">
                    {["Goodman", "Daikin", "Midea", "Furnaces", "Air Handlers", "Coils"].map((t) => (
                      <Link key={t} to={`/shop?q=${encodeURIComponent(t)}`} className="rounded-sm border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:border-sky-500 hover:text-sky-700">{t}</Link>
                    ))}
                  </div>
                )}
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  {hasActiveFilters && (
                    <Button className="rounded-sm bg-sky-700 hover:bg-sky-800" onClick={clearAll} data-testid="empty-clear-filters">{search.trim() ? "Browse all equipment" : "Clear filters"}</Button>
                  )}
                  <Button asChild variant="outline" className="rounded-sm border-slate-300"><a href={SITE.phoneHref}><Phone className="h-4 w-4" /> {SITE.phone}</a></Button>
                  <Button asChild variant={hasActiveFilters ? "ghost" : "default"} className={`rounded-sm ${hasActiveFilters ? "text-sky-700" : "bg-sky-700 hover:bg-sky-800"}`}><Link to="/contact">Contact Us</Link></Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4" data-testid="product-grid">
                {products.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
